import { useInfiniteQuery, UseInfiniteQueryResult } from 'react-query';
import pDebounce from 'p-debounce';

import { getTokenWithType } from './useAuth';

import { getGlobalSurvey } from '../utils/urls';

import { GlobalSurveyType } from 'types/forms';
import { ErrorModal, SortItemType } from 'types/modalTypes';

const pageLength = 100;

function getColumnFromId(id: string) {
  const idsList = ['Title', 'Notes', 'Call taker can add to call'];
  const index = idsList.findIndex(item => item === id);
  return `${index}`;
}

async function fetchGlobalSurvey({
  requestData,
  pageParam,
}: TVariables): Promise<TResult> {
  requestData.append('start', `${pageParam}`);
  const response = await fetch(getGlobalSurvey, {
    method: 'POST',
    headers: {
      Authorization: getTokenWithType(),
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: requestData,
  });
  return response.json();
}

const debouncedFetch = pDebounce(fetchGlobalSurvey, 500);

type TError = ErrorModal;

type TResult = {
  Data: Array<GlobalSurveyType>;
  Draw: number;
  FilteredRecords: number;
  IsAutoRefreshEnabled: boolean;
  TotalRecords: number;
  PageNumber: number;
  IsSuccess: boolean;
  StatusCode: number;
};

type TVariables = {
  requestData: any;
  pageParam: number;
};

function formatRequestData(
  keywordSearch: string,
  columnsSearch: Record<string, string>,
  sortedColumn: string,
  sortOrder: string,
  sortedColumn1?: string,
  sortOrder1?: string,
): FormData {
  const formData: any = new URLSearchParams();

  formData.append('draw', '1');
  formData.append('columns[0][data]', 'Title');
  formData.append('columns[0][name]', '');
  formData.append('columns[0][searchable]', 'true');
  formData.append('columns[0][orderable]', 'true');
  formData.append(
    'columns[0][search][value]',
    columnsSearch && columnsSearch['Title'] ? columnsSearch['Title'] : '',
  );

  formData.append('columns[1][data]', 'Notes');
  formData.append('columns[1][name]', 'Notes');
  formData.append('columns[1][searchable]', 'true');
  formData.append('columns[1][orderable]', 'true');
  formData.append(
    'columns[1][search][value]',
    columnsSearch && columnsSearch['Notes'] ? columnsSearch['Notes'] : '',
  );
  formData.append('columns[1][search][regex]', 'false');

  formData.append('columns[2][data]', 'IsClinicianAllowedToAddToCall');
  formData.append('columns[2][name]', '');
  formData.append('columns[2][searchable]', 'true');
  formData.append('columns[2][orderable]', 'true');
  formData.append(
    'columns[2][search][value]',
    columnsSearch && columnsSearch['Call taker can add to call']
      ? columnsSearch['Call taker can add to call']
      : '',
  );
  formData.append('columns[2][search][regex]', 'false');

  formData.append('order[0][column]', sortedColumn);
  formData.append('order[0][dir]', sortOrder);

  if (sortedColumn1 && sortOrder1) {
    formData.append('order[1][column]', sortedColumn1); // for initial fetch and reset
    formData.append('order[1][dir]', sortOrder1);
  }
  formData.append('search[value]', `${keywordSearch}`);
  formData.append('search[regex]', 'false');
  formData.append('length', `${100}`);
  formData.append('timezoneOffset', '-330');
  return formData;
}

function useGetGlobalSurveys({
  keywordSearch,
  columnsSearch,
  sortItem,
}: {
  keywordSearch: string;
  columnsSearch: Record<string, string>;
  sortItem: SortItemType | Array<SortItemType>;
}): UseInfiniteQueryResult<TResult, TError> {
  let requestData = {};
  if (sortItem instanceof Array) {
    requestData = formatRequestData(
      keywordSearch,
      columnsSearch,
      getColumnFromId(sortItem[0].sortKey),
      sortItem[0].sortOrder,
    );
  } else if (sortItem.sortKey && sortItem.sortOrder) {
    requestData = formatRequestData(
      keywordSearch,
      columnsSearch,
      getColumnFromId(sortItem.sortKey),
      sortItem.sortOrder,
    );
  }

  return useInfiniteQuery(
    ['global_survey_list', keywordSearch, columnsSearch, sortItem],
    ({ pageParam = 0 }) => debouncedFetch({ requestData, pageParam }),
    {
      getNextPageParam: (lastPage: TResult, allPages: Array<TResult>) => {
        if (lastPage !== null && lastPage?.Data?.length === pageLength)
          return allPages?.length * pageLength;
      },
      getPreviousPageParam: (firstPage: TResult, allPages: Array<TResult>) => {
        if (firstPage !== null && firstPage?.Data?.length === pageLength)
          return allPages?.length - 1 * pageLength;
      },
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );
}

export { useGetGlobalSurveys };
